import type { ReactNode } from 'react';

import type { RoleItem } from './type';

export class TreeNode {
  public id: string;
  public name: string;
  public parentNode?: TreeNode;
  public childNodes: TreeNode[] = [];

  public constructor({ id, name }: RoleItem) {
    this.id = id.toString();
    this.name = name;
  }

  public get parentNodes() {
    const parentNodes: TreeNode[] = [];

    if (this.parentNode) {
      parentNodes.push(...[...this.parentNode.parentNodes, this.parentNode]);
    }

    return parentNodes;
  }

  public get brotherNodes() {
    return this.parentNode ? this.parentNode.childNodes.filter((item) => item.id != this.id) : [];
  }

  public get flattenChildNodes() {
    const flattenChildNodes: TreeNode[] = [];

    this.childNodes.forEach((childNode) => {
      flattenChildNodes.push(childNode, ...childNode.flattenChildNodes);
    });

    return flattenChildNodes;
  }

  public get key(): string {
    return this.id.toString();
  }

  public get completeName(): string {
    return `${this.parentNode ? `${this.parentNode.completeName}-` : ''}${this.name}`;
  }

  public get hasChildren() {
    return this.childNodes.length !== 0;
  }

  public get treeData(): {
    key: string;
    title: ReactNode;
    children?: TreeNode['treeData'][];
  } {
    return {
      key: this.id,
      title: this.name,
      children: this.childNodes.map((childNode) => childNode.treeData),
    };
  }

  public setParent(parentNode: TreeNode) {
    this.parentNode = parentNode;
  }

  public setChildren(childNodes: TreeNode[]) {
    this.childNodes = childNodes;
  }
}

export class DataTree {
  public nodes: TreeNode[] = [];
  private nodesMap = new Map<string, TreeNode>();

  public constructor(nodes: RoleItem[], isFilterDisabled: boolean) {
    const traverse = (tempNodes: RoleItem[], parentNode?: TreeNode) => {
      return (isFilterDisabled ? tempNodes.filter((it) => it.enabled) : tempNodes).map((node) => {
        const treeNode = new TreeNode(node);
        if (parentNode) {
          treeNode.setParent(parentNode);
        }
        this.nodesMap.set(treeNode.id, treeNode);
        if (node.children) {
          treeNode.setChildren(traverse(node.children, treeNode));
        }

        return treeNode;
      });
    };
    this.nodes = traverse(nodes);
  }

  public get data() {
    return this.nodes.map((node) => node.treeData);
  }

  public getNodesById(id: string) {
    return [...this.nodesMap.values()].filter((node) => node.id === id);
  }

  public getNode(key: string) {
    return this.nodesMap.get(key);
  }
}
